/**
 * ============================================================
 * SPIRITUAL FREE BOOK PICKER
 * ============================================================
 *
 * RULE
 *
 * 1 x Spiritual product
 * =
 * 1 x free Book
 *
 *
 * UX
 *
 * If 1 gift is available:
 * -> normal "Choose this book" button
 * -> Book is added immediately
 *
 * If 2+ gifts are available:
 * -> quantity controls appear directly on every Book card
 * -> customer selects exactly X books
 * -> one button adds all selected Books
 *
 *
 * CART PROTECTION
 *
 * - Books without a valid Spiritual parent are removed.
 * - A Spiritual line can never have more Books than
 *   its own quantity.
 * - If Spiritual quantity decreases, excess Books
 *   are automatically removed.
 *
 * ============================================================
 */

(() => {

  'use strict';


  const SPIRITUAL_TYPE = 'spiritual';
  const BOOK_TYPE = 'books';


  let addInProgress = false;
  let syncInProgress = false;


  /**
   * Temporary selection used only before
   * the customer confirms multiple Books.
   *
   * Map:
   *
   * variantId => quantity
   */
  const multiSelection =
    new Map();


  /**
   * Number of Books that currently
   * need to be selected.
   */
  let multiLimit = 0;


  /**
   * Used to detect when cart entitlement changes.
   */
  let lastGiftLimit = null;



  /**
   * ============================================================
   * TEXTS
   * ============================================================
   *
   * Read from [data-spiritual-gift-i18n] in the Liquid snippet,
   * so RO / EN live in a single place.
   */


  let i18n = null;


  function t(
    key,
    n
  ) {

    if (!i18n) {

      const node =
        document.querySelector(
          '[data-spiritual-gift-i18n]'
        );


      try {

        i18n =
          node
            ? JSON.parse(node.textContent)
            : {};

      } catch (error) {

        i18n = {};

      }

    }


    return String(
      i18n[key] || ''
    ).replace(
      '[n]',
      n
    );

  }



  /**
   * ============================================================
   * SHOPIFY CART
   * ============================================================
   */


  async function getCart() {

    const response =
      await fetch(
        window.Shopify.routes.root +
        'cart.js',
        {
          headers: {
            'Accept':
              'application/json'
          },

          cache:
            'no-store'
        }
      );


    if (!response.ok) {

      throw new Error(
        'Could not load Shopify cart.'
      );

    }


    return response.json();

  }



  function isType(
    item,
    type
  ) {

    return (
      item.product_type &&
      item.product_type
        .trim()
        .toLowerCase() ===
        type
    );

  }



  /**
   * ============================================================
   * CART STATE
   * ============================================================
   */


  function getGiftState(cart) {

    const parents =
      new Map();


    const invalidBooks =
      [];



    /**
     * Find all Spiritual parent lines.
     */
    cart.items.forEach(item => {

      if (
        isType(
          item,
          SPIRITUAL_TYPE
        )
        &&
        !item.parent_relationship
      ) {

        parents.set(
          item.key,
          {

            item:
              item,

            allowedQty:
              Number(
                item.quantity || 0
              ),

            bookQty:
              0,

            books:
              []

          }
        );

      }

    });



    /**
     * Analyse all Books.
     */
    cart.items.forEach(item => {

      if (
        !isType(
          item,
          BOOK_TYPE
        )
      ) {

        return;

      }



      const parentKey =
        item
          .parent_relationship
          ?.parent_key;



      /**
       * Valid nested Book.
       */
      if (
        parentKey &&
        parents.has(parentKey)
      ) {

        const parent =
          parents.get(parentKey);


        parent.bookQty +=
          Number(
            item.quantity || 0
          );


        parent.books.push(
          item
        );


        return;

      }



      /**
       * Book without Spiritual parent.
       */
      invalidBooks.push(
        item
      );

    });



    let spiritualQty = 0;
    let validBookQty = 0;
    let giftsLeft = 0;



    parents.forEach(parent => {

      spiritualQty +=
        parent.allowedQty;


      validBookQty +=
        parent.bookQty;


      giftsLeft +=
        Math.max(
          0,
          parent.allowedQty -
          parent.bookQty
        );

    });



    return {

      parents,
      invalidBooks,

      spiritualQty,
      validBookQty,
      giftsLeft

    };

  }



  /**
   * ============================================================
   * SELECTION STATE
   * ============================================================
   */


  function resetMultiSelection() {

    multiSelection.clear();

  }



  function getSelectedTotal() {

    let total = 0;


    multiSelection.forEach(
      quantity => {

        total +=
          Number(quantity || 0);

      }
    );


    return total;

  }



  /**
   * ============================================================
   * UI
   * ============================================================
   */


  function getWidget() {

    return document.querySelector(
      '#spiritual-gift-picker'
    );

  }



  function getGiftCards(widget) {

    return Array.from(
      widget.querySelectorAll(
        '[data-spiritual-gift-card]'
      )
    );

  }



  /**
   * Render current multiple Book selection.
   */
  function renderMultiSelection(
    widget
  ) {

    if (!widget) {

      return;

    }


    const selectedTotal =
      getSelectedTotal();



    getGiftCards(widget)
      .forEach(card => {

        const variantId =
          Number(
            card.dataset.variantId
          );


        const available =
          card.dataset.available ===
          'true';


        const quantity =
          multiSelection.get(
            variantId
          ) || 0;



        /**
         * Selected visual state.
         */
        card.classList.toggle(
          'is-selected',
          quantity > 0
        );



        /**
         * Number.
         */
        const count =
          card.querySelector(
            '[data-spiritual-gift-count]'
          );


        if (count) {

          count.textContent =
            String(quantity);

        }



        /**
         * Minus button.
         */
        const minus =
          card.querySelector(
            '[data-spiritual-gift-minus]'
          );


        if (minus) {

          minus.disabled =
            addInProgress ||
            quantity <= 0;

        }



        /**
         * Plus button.
         */
        const plus =
          card.querySelector(
            '[data-spiritual-gift-plus]'
          );


        if (plus) {

          plus.disabled =
            addInProgress ||
            !available ||
            selectedTotal >=
              multiLimit;

        }

      });



    /**
     * X / Y counter.
     */
    const selectedText =
      widget.querySelector(
        '[data-spiritual-selected-total]'
      );


    if (selectedText) {

      selectedText.textContent =
        selectedTotal +
        ' / ' +
        multiLimit;

    }



    /**
     * Main add button.
     */
    const addButton =
      widget.querySelector(
        '[data-spiritual-add-selected]'
      );


    if (addButton) {

      addButton.disabled =
        addInProgress ||
        selectedTotal !==
          multiLimit;


      addButton.textContent =
        t(
          'addMany',
          multiLimit
        );

    }

  }



  /**
   * Switch cards between:
   *
   * SINGLE MODE
   * and
   * MULTI MODE.
   */
  function setPickerMode(
    widget,
    mode
  ) {

    widget.dataset.giftMode =
      mode;



    getGiftCards(widget)
      .forEach(card => {

        const chooseButton =
          card.querySelector(
            '[data-spiritual-gift-button]'
          );


        const quantityControls =
          card.querySelector(
            '[data-spiritual-gift-qty-controls]'
          );



        if (
          mode === 'single'
        ) {

          if (chooseButton) {

            chooseButton.hidden =
              false;

          }


          if (quantityControls) {

            quantityControls.hidden =
              true;

          }


          card.classList.remove(
            'is-selected'
          );

        }



        if (
          mode === 'multi'
        ) {

          if (chooseButton) {

            chooseButton.hidden =
              true;

          }


          if (quantityControls) {

            quantityControls.hidden =
              false;

          }

        }

      });



    const footer =
      widget.querySelector(
        '[data-spiritual-multi-footer]'
      );


    if (footer) {

      footer.hidden =
        mode !== 'multi';

    }

  }



  /**
   * ============================================================
   * CART DRAWER PROMPT
   * ============================================================
   *
   * After "Add to cart" the theme opens the cart drawer over
   * the page, so customers never see the picker and order
   * without their free book.
   *
   * The prompt is placed right after the drawer <header>,
   * outside the [data-ajax-cart-section] that Liquid Ajax Cart
   * re-renders on every request, so it survives cart updates.
   *
   * 1 book left  -> books are chosen directly in the drawer
   *                 (same data-spiritual-gift-button handler)
   * 2+ books     -> button that leads back to the page picker
   */


  function getCartDrawerHeader() {

    return document.querySelector(
      '#cartBox dialog-wrapper > header'
    );

  }



  function renderCartPrompt(
    widget,
    giftsLeft
  ) {

    const header =
      getCartDrawerHeader();


    if (!header) {

      return;

    }



    let prompt =
      header.parentElement
        .querySelector(
          ':scope > [data-spiritual-gift-cart]'
        );



    if (
      giftsLeft <= 0
    ) {

      if (prompt) {

        prompt.remove();

      }


      return;

    }



    if (!prompt) {

      const template =
        widget.querySelector(
          '[data-spiritual-gift-cart-tpl]'
        );


      if (!template) {

        return;

      }


      prompt =
        template.content
          .firstElementChild
          .cloneNode(true);


      header.after(
        prompt
      );

    }



    const single =
      giftsLeft === 1;


    prompt.querySelector(
      '[data-spiritual-gift-cart-single]'
    ).hidden =
      !single;


    prompt.querySelector(
      '[data-spiritual-gift-cart-cta]'
    ).hidden =
      single;


    prompt.querySelector(
      '[data-spiritual-gift-cart-text]'
    ).textContent =
      single
        ? t('cartTextOne')
        : t('leftMany', giftsLeft);

  }



  /**
   * Close the drawer and bring the page picker into view.
   */
  function goToPagePicker() {

    const widget =
      getWidget();


    if (!widget) {

      return;

    }



    document
      .querySelector(
        '#cartBox [data-close]'
      )
      ?.click();



    /**
     * Let the theme close the drawer and
     * unlock page scrolling first.
     */
    setTimeout(() => {

      widget.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });


      widget.classList.remove(
        'is-highlight'
      );


      // restart the animation on repeated clicks
      void widget.offsetWidth;


      widget.classList.add(
        'is-highlight'
      );

    }, 350);

  }



  /**
   * Main product-page picker update.
   */
  function updateGiftPicker(cart) {

    const widget =
      getWidget();


    if (!widget) {

      return;

    }



    const state =
      getGiftState(cart);



    renderCartPrompt(
      widget,
      state.giftsLeft
    );



    const emptyMessage =
      widget.querySelector(
        '[data-gift-empty]'
      );


    const picker =
      widget.querySelector(
        '[data-gift-picker]'
      );


    const giftsLeftText =
      widget.querySelector(
        '[data-gifts-left-text]'
      );



    /**
     * Entitlement changed.
     *
     * Example:
     * quantity 3 -> quantity 2
     *
     * Discard unfinished selection.
     */
    if (
      lastGiftLimit !==
      state.giftsLeft
    ) {

      resetMultiSelection();


      lastGiftLimit =
        state.giftsLeft;

    }



    /**
     * No Spiritual product yet.
     */
    if (
      state.spiritualQty === 0
    ) {

      widget.hidden =
        false;


      if (emptyMessage) {

        emptyMessage.hidden =
          false;

      }


      if (picker) {

        picker.hidden =
          true;

      }


      resetMultiSelection();

      multiLimit = 0;


      return;

    }



    /**
     * All available gifts already selected.
     */
    if (
      state.giftsLeft <= 0
    ) {

      resetMultiSelection();

      multiLimit = 0;

      widget.hidden =
        true;


      return;

    }



    /**
     * Gifts remain.
     */
    widget.hidden =
      false;


    if (emptyMessage) {

      emptyMessage.hidden =
        true;

    }


    if (picker) {

      picker.hidden =
        false;

    }



    /**
     * Exactly one Book remains.
     *
     * Keep very simple UX.
     */
    if (
      state.giftsLeft === 1
    ) {

      multiLimit =
        1;


      resetMultiSelection();


      setPickerMode(
        widget,
        'single'
      );


      if (giftsLeftText) {

        giftsLeftText.textContent =
          t('leftOne');

      }


      return;

    }



    /**
     * Multiple Books remain.
     */
    multiLimit =
      state.giftsLeft;


    setPickerMode(
      widget,
      'multi'
    );


    if (giftsLeftText) {

      giftsLeftText.textContent =
        t(
          'leftMany',
          state.giftsLeft
        );

    }


    renderMultiSelection(
      widget
    );

  }



  /**
   * ============================================================
   * PLUS / MINUS
   * ============================================================
   */


  function changeMultiSelection(
    variantId,
    difference
  ) {

    if (
      addInProgress ||
      multiLimit <= 1
    ) {

      return;

    }



    variantId =
      Number(variantId);


    if (!variantId) {

      return;

    }



    const widget =
      getWidget();


    if (!widget) {

      return;

    }



    const current =
      multiSelection.get(
        variantId
      ) || 0;


    const selectedTotal =
      getSelectedTotal();



    /**
     * PLUS
     */
    if (
      difference > 0
    ) {

      if (
        selectedTotal >=
        multiLimit
      ) {

        return;

      }


      multiSelection.set(
        variantId,
        current + 1
      );

    }



    /**
     * MINUS
     */
    if (
      difference < 0
    ) {

      if (
        current <= 0
      ) {

        return;

      }


      const newQuantity =
        current - 1;


      if (
        newQuantity <= 0
      ) {

        multiSelection.delete(
          variantId
        );

      } else {

        multiSelection.set(
          variantId,
          newQuantity
        );

      }

    }



    renderMultiSelection(
      widget
    );

  }



  /**
   * ============================================================
   * LIQUID AJAX CART
   * ============================================================
   */


  function ajaxCartAdd(body) {

    return new Promise(resolve => {

      window
        .liquidAjaxCart
        .add(
          body,
          {

            important:
              true,

            lastCallback:
              function() {

                resolve();

              }

          }
        );

    });

  }



  function ajaxCartChange(body) {

    return new Promise(resolve => {

      window
        .liquidAjaxCart
        .change(
          body,
          {

            important:
              true,

            lastCallback:
              function() {

                resolve();

              }

          }
        );

    });

  }



  /**
   * Disable all controls while Shopify is updating.
   */
  function setControlsDisabled(
    disabled
  ) {

    const widget =
      getWidget();


    if (!widget) {

      return;

    }



    /**
     * document, not widget: the cart drawer
     * prompt has the same buttons.
     */
    document
      .querySelectorAll(
        '[data-spiritual-gift-button]'
      )
      .forEach(button => {

        button.disabled =
          disabled ||
          button
            .closest(
              '[data-spiritual-gift-card]'
            )
            ?.dataset
            .available !== 'true';

      });



    if (
      !disabled &&
      widget.dataset.giftMode ===
        'multi'
    ) {

      renderMultiSelection(
        widget
      );

    }

  }



  /**
   * ============================================================
   * CART CORRECTION
   * ============================================================
   */


  function findCartCorrection(cart) {

    const state =
      getGiftState(cart);



    /**
     * Book without Spiritual parent.
     */
    if (
      state.invalidBooks.length > 0
    ) {

      const book =
        state.invalidBooks[0];


      return {

        id:
          book.key,

        quantity:
          0

      };

    }



    /**
     * More Books than allowed
     * for a particular Spiritual parent.
     */
    for (
      const parent
      of state.parents.values()
    ) {

      if (
        parent.bookQty <=
        parent.allowedQty
      ) {

        continue;

      }



      const excess =
        parent.bookQty -
        parent.allowedQty;



      const book =
        parent.books[
          parent.books.length - 1
        ];



      return {

        id:
          book.key,

        quantity:
          Math.max(
            0,
            book.quantity -
            excess
          )

      };

    }



    return null;

  }



  /**
   * Keep cart valid after every Shopify cart change.
   */
  async function syncGiftCart() {

    if (
      syncInProgress ||
      addInProgress
    ) {

      return;

    }



    syncInProgress =
      true;


    let finalCart =
      null;



    try {

      /**
       * One correction at a time.
       */
      for (
        let i = 0;
        i < 10;
        i++
      ) {

        const cart =
          await getCart();


        const correction =
          findCartCorrection(
            cart
          );


        if (!correction) {

          finalCart =
            cart;

          break;

        }



        await ajaxCartChange(
          correction
        );

      }



      if (!finalCart) {

        finalCart =
          await getCart();

      }


    } catch (error) {

      console.error(
        'Spiritual gift sync error:',
        error
      );


      try {

        finalCart =
          await getCart();

      } catch (
        secondaryError
      ) {

        console.error(
          'Could not reload Spiritual gift cart:',
          secondaryError
        );

      }


    } finally {

      syncInProgress =
        false;

    }



    if (finalCart) {

      updateGiftPicker(
        finalCart
      );

    }

  }



  /**
   * ============================================================
   * SINGLE BOOK
   * ============================================================
   */


  function findAvailableParent(
    cart
  ) {

    const state =
      getGiftState(cart);


    for (
      const parent
      of state.parents.values()
    ) {

      if (
        parent.bookQty <
        parent.allowedQty
      ) {

        return parent.item;

      }

    }


    return null;

  }



  async function addSingleGiftBook(
    variantId
  ) {

    if (
      addInProgress ||
      syncInProgress ||
      !window.liquidAjaxCart
    ) {

      return;

    }



    addInProgress =
      true;


    setControlsDisabled(
      true
    );



    try {

      const cart =
        await getCart();


      const state =
        getGiftState(cart);



      if (
        state.giftsLeft <= 0
      ) {

        updateGiftPicker(
          cart
        );

        return;

      }



      const parent =
        findAvailableParent(
          cart
        );


      if (!parent) {

        return;

      }



      await ajaxCartAdd({

        items: [

          {

            id:
              Number(
                variantId
              ),

            quantity:
              1,


            /**
             * Native Shopify nested cart
             * relationship.
             */
            parent_line_key:
              parent.key,


            properties: {

              '_spiritual_gift':
                'true'

            }

          }

        ]

      });


    } catch (error) {

      console.error(
        'Could not add Spiritual gift:',
        error
      );


    } finally {

      addInProgress =
        false;


      setControlsDisabled(
        false
      );

    }



    await syncGiftCart();

  }



  /**
   * ============================================================
   * MULTIPLE BOOKS
   * ============================================================
   */


  /**
   * Distribute selected Books across
   * available Spiritual parent lines.
   */
  function buildBatchGiftItems(
    state,
    selection
  ) {

    const parentSlots =
      [];


    state.parents
      .forEach(parent => {

        const remaining =
          Math.max(
            0,
            parent.allowedQty -
            parent.bookQty
          );


        if (
          remaining > 0
        ) {

          parentSlots.push({

            key:
              parent.item.key,

            remaining:
              remaining

          });

        }

      });



    const items =
      [];



    for (
      const [
        variantId,
        selectedQty
      ]
      of selection.entries()
    ) {

      let quantityLeft =
        selectedQty;



      for (
        const parent
        of parentSlots
      ) {

        if (
          quantityLeft <= 0
        ) {

          break;

        }


        if (
          parent.remaining <= 0
        ) {

          continue;

        }



        const quantity =
          Math.min(
            quantityLeft,
            parent.remaining
          );



        items.push({

          id:
            Number(
              variantId
            ),

          quantity:
            quantity,

          parent_line_key:
            parent.key,

          properties: {

            '_spiritual_gift':
              'true'

          }

        });



        quantityLeft -=
          quantity;


        parent.remaining -=
          quantity;

      }



      if (
        quantityLeft > 0
      ) {

        throw new Error(
          'Not enough Spiritual gift slots.'
        );

      }

    }



    return items;

  }



  async function addSelectedGiftBooks() {

    if (
      addInProgress ||
      syncInProgress ||
      !window.liquidAjaxCart
    ) {

      return;

    }



    const selectedTotal =
      getSelectedTotal();



    if (
      selectedTotal !==
      multiLimit
    ) {

      return;

    }



    addInProgress =
      true;


    setControlsDisabled(
      true
    );



    try {

      /**
       * Always re-read Shopify cart
       * before actually adding.
       */
      const cart =
        await getCart();


      const state =
        getGiftState(cart);



      /**
       * Cart changed while customer
       * was selecting Books.
       */
      if (
        state.giftsLeft !==
        selectedTotal
      ) {

        resetMultiSelection();


        updateGiftPicker(
          cart
        );


        return;

      }



      const items =
        buildBatchGiftItems(
          state,
          multiSelection
        );



      if (
        items.length === 0
      ) {

        return;

      }



      await ajaxCartAdd({

        items:
          items

      });



      /**
       * Temporary selection no longer matters.
       * Shopify cart is now the source of truth.
       */
      resetMultiSelection();


    } catch (error) {

      console.error(
        'Could not add selected Spiritual gifts:',
        error
      );


    } finally {

      addInProgress =
        false;


      setControlsDisabled(
        false
      );

    }



    await syncGiftCart();

  }



  /**
   * ============================================================
   * EVENTS
   * ============================================================
   */


  document.addEventListener(
    'click',
    event => {


      /**
       * MINUS
       */
      const minus =
        event.target.closest(
          '[data-spiritual-gift-minus]'
        );


      if (minus) {

        event.preventDefault();


        changeMultiSelection(
          minus.dataset
            .spiritualGiftMinus,
          -1
        );


        return;

      }



      /**
       * PLUS
       */
      const plus =
        event.target.closest(
          '[data-spiritual-gift-plus]'
        );


      if (plus) {

        event.preventDefault();


        changeMultiSelection(
          plus.dataset
            .spiritualGiftPlus,
          1
        );


        return;

      }



      /**
       * ADD MULTIPLE
       */
      const addSelected =
        event.target.closest(
          '[data-spiritual-add-selected]'
        );


      if (addSelected) {

        event.preventDefault();


        if (
          !addSelected.disabled
        ) {

          addSelectedGiftBooks();

        }


        return;

      }



      /**
       * CART DRAWER -> PAGE PICKER (2+ books)
       */
      const cartCta =
        event.target.closest(
          '[data-spiritual-gift-cart-cta]'
        );


      if (cartCta) {

        event.preventDefault();


        goToPagePicker();


        return;

      }



      /**
       * SINGLE BOOK
       *
       * Page picker and cart drawer prompt.
       */
      const button =
        event.target.closest(
          '[data-spiritual-gift-button]'
        );


      if (!button) {

        return;

      }



      event.preventDefault();



      const widget =
        getWidget();


      if (
        widget?.dataset.giftMode ===
        'single'
      ) {

        addSingleGiftBook(
          button.dataset.variantId
        );

      }

    }
  );



  /**
   * ============================================================
   * INIT
   * ============================================================
   */


  async function initGiftSystem() {

    await syncGiftCart();

  }



  if (
    window.liquidAjaxCart?.init
  ) {

    initGiftSystem();

  } else {

    document.addEventListener(
      'liquid-ajax-cart:init',
      initGiftSystem,
      {
        once: true
      }
    );

  }



  /**
   * Any cart change made by Ethereal.
   *
   * Examples:
   *
   * + quantity
   * - quantity
   * remove
   * add
   */
  document.addEventListener(
    'liquid-ajax-cart:request-end',
    () => {

      if (
        addInProgress ||
        syncInProgress
      ) {

        return;

      }


      syncGiftCart();

    }
  );


})();